/**
 * Standards picker logic, for an activity (`{ screenId, activityId }`) or a rubric (`{ rubricId }`).
 * Stores codes only.
 *
 *  1. One grade at a time, the template's by default; the picker's grade never writes the template's.
 *  2. Nothing is dropped: an off-grade stored code stays as a plain, disabled-to-re-add chip (B12.5.25).
 *  3. Natural-order sort (`RL.4.9` before `RL.4.10`).
 *  4. A cluster pick is not expanded to its leaves (B12.2.8).
 *
 * No cluster badge in the dropdown — it was removed as noise (every ELA parent is top-level).
 */
import { computed, ref } from 'vue'

import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { Activity, AssessmentStandardOption } from './interfaces'
import type { ReviewRubric } from './types'
import type { Grade } from './enums'

import { useAssessmentEditorStore } from './store'
import { gradeOptions } from './assessment-template-options'
import {
  useStandardLabelLookup,
  useStandardsCatalogue,
} from './standards-catalogue.hook'

/** Which `standards` array the picker addresses. A discriminated union, so a caller cannot
 *  half-name a target the way two optional ids would allow. */
export type StandardOwner =
  | { screenId: string, activityId: string, rubricId?: undefined }
  | { rubricId: string, screenId?: undefined, activityId?: undefined }

export interface StandardSelectProps {
  owner: StandardOwner
  /** The kit's own label row. Omitted where a heading above the field already names it. */
  label?: string
}

interface UseStandardSelectOptions {
  owner: () => StandardOwner
}

/** One tagged standard, as a chip below the input. */
interface StandardChip {
  code: string
  /** The full statement — the chip's `title`, never its text. */
  label: string
}

/** An option row. `statement` is the one thing the dropdown renders beyond the kit's own text. */
export interface StandardSelectOption extends AppSelectOption<string> {
  /** The full statement, rendered as the option's second line. */
  statement: string
}

interface UseStandardSelectReturn {
  refOptions: ComputedRef<StandardSelectOption[]>
  refSelectedCodes: ComputedRef<string[]>
  refChips: ComputedRef<StandardChip[]>
  refIsLoading: ComputedRef<boolean>
  refIsDisabled: ComputedRef<boolean>
  refPlaceholder: ComputedRef<string>
  /** The grade whose catalogue the picker offers (rule 1). Writable for `v-model` on `AppSelect`;
   *  `undefined` only while no template is loaded, and an `undefined` write is ignored. */
  refCatalogueGrade: WritableComputedRef<Grade | undefined>
  gradeSelectOptions: AppSelectOption<Grade>[]
  setSelectedCodes: (codes: string[]) => void
  removeCode: (code: string) => void
}

/** `localeCompare` with `numeric` so `RL.4.9` sorts before `RL.4.10`. */
const byNaturalCode = (a: { code: string }, b: { code: string }): number =>
  a.code.localeCompare(b.code, undefined, { numeric: true })

export const useStandardSelect = (
  { owner }: UseStandardSelectOptions,
): UseStandardSelectReturn => {
  const store = useAssessmentEditorStore()

  const refActivity = computed<Activity | null>(() => {
    const target = owner()
    if (target.rubricId !== undefined) {
      return null
    }
    return store.getScreens
      .find(screen => screen.id === target.screenId)
      ?.activities.find(activity => activity.id === target.activityId) ?? null
  })

  const refRubric = computed<ReviewRubric | null>(() => {
    const target = owner()
    return target.rubricId === undefined ? null : store.getRubricCatalog[target.rubricId] ?? null
  })

  /** The author's grade override, `null` while the picker still follows the template (rule 1). */
  const gradeOverride = ref<Grade | null>(null)

  const refCatalogueGrade = computed<Grade | undefined>({
    // `undefined`, not `null`: `AppSelect`'s model is `TValue | undefined`.
    get: () => gradeOverride.value ?? store.getGrade ?? undefined,
    set: (value) => {
      // The select cannot clear; an `undefined` write is ignored rather than emptying the catalogue.
      if (value) {
        gradeOverride.value = value
      }
    },
  })

  const { refStandards, refIsLoading } = useStandardsCatalogue({
    disciplineCode: () => store.getDisciplineCode,
    grade: () => refCatalogueGrade.value ?? null,
  })

  /** Statements across every grade browsed this session — see rule 2. */
  const refLabelLookup = useStandardLabelLookup()

  /** The document's own codes. Never filtered against the catalogue — see rule 2. */
  const refCodes = computed<string[]>(() =>
    (refRubric.value?.standards ?? refActivity.value?.standards ?? []),
  )

  /** The stored codes with their statements for display. `label` is never written back. */
  const refStored = computed<StandardChip[]>(() =>
    refCodes.value.map(code => ({ code, label: refLabelLookup.value(code) ?? '' })),
  )

  const refSelectedCodes = computed<string[]>(() => refCodes.value)

  /** Chip order follows the document, not the sort: the author's own order is information. */
  const refChips = computed<StandardChip[]>(() => refStored.value)

  /**
   * The offerable rows, plus any stored code the current grade's catalogue does not contain. Those
   * have to be in `options` even though they cannot be picked: the kit derives "selected" from the
   * option list, so omitting them would drop them on the next change. They are appended `disabled`
   * — removable, not re-addable from this grade (B12.5.25).
   */
  const refOptions = computed<StandardSelectOption[]>(() => {
    const offered = [...refStandards.value].sort(byNaturalCode)
    const offeredCodes = new Set(offered.map(standard => standard.code))

    const stranded = refStored.value
      .filter(standard => !offeredCodes.has(standard.code))
      .sort(byNaturalCode)

    return [
      ...offered.map((standard: AssessmentStandardOption): StandardSelectOption => ({
        value: standard.code,
        text: standard.code,
        statement: standard.label,
      })),
      ...stranded.map((standard): StandardSelectOption => ({
        value: standard.code,
        text: standard.code,
        statement: standard.label,
        disabled: true,
      })),
    ]
  })

  const refIsDisabled = computed<boolean>(() =>
    store.getDisciplineCode === null || refCatalogueGrade.value === undefined,
  )

  const refPlaceholder = computed<string>(() =>
    refIsLoading.value ? 'Loading standards…' : 'Search by code or wording',
  )

  /** The kit hands back the whole selection, and it goes to the store as codes — the only shape
   *  either field is written in. */
  const setSelectedCodes = (codes: string[]): void => {
    const target = owner()
    if (target.rubricId !== undefined) {
      store.setRubricStandards(target.rubricId, codes)
      return
    }
    store.setActivityStandards(target.screenId, target.activityId, codes)
  }

  const removeCode = (code: string): void => {
    setSelectedCodes(refSelectedCodes.value.filter(selected => selected !== code))
  }

  return {
    refOptions,
    refSelectedCodes,
    refChips,
    refIsLoading,
    refIsDisabled,
    refPlaceholder,
    refCatalogueGrade,
    gradeSelectOptions: gradeOptions,
    setSelectedCodes,
    removeCode,
  }
}
